import {
  Button,
  DefaultCommunityImage,
  EventShareOptionsTooltip,
  NumberInput,
  PopupEventWrapper,
  TextArea,
  Tooltip,
} from 'components';
import InfoPopupV2 from 'components/Popups/InfoPopupv2';
import { EditEventType } from 'constants/sidebars';
import moment from 'moment';
import { HighlightText } from 'pages/PersonalChat/ChatUserList';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { FileIcon } from 'static';
import { openModal } from 'store/actionCreators/modal';
import {
  CommunityListItem,
  EventReply,
  EventResponse,
  EventTask,
  EventType,
  FullUser,
  MyAppointmentsEvent,
} from 'types';
import { getDateString, getTimeInterval } from 'utils';
import { generatePdf } from 'utils/pdf';

import { SHARE_POPUP } from '../../constants/popups';
import { getSettings } from '../../store/selectors/auth';
import { FIELDS, ReplyFormData, useReplyForm } from './form';
import ReplyBlock from './ReplyBlock';
import { CommunityTitle, EventCard, GroupTitle } from './styled';
import TasksList from './TasksList';
interface EventCardProps {
  event: MyAppointmentsEvent;
  currentUser: FullUser;
  community: CommunityListItem;
  eventResponses: { [p: string]: EventResponse[] };
  tasks: EventTask[];
  eventTypes: EventType[];
  isLoading: boolean;
  zoom: number;
  onSubmit: (
    type: EditEventType,
    data: ReplyFormData,
    event: MyAppointmentsEvent,
    reply: {
      reply: EventReply & { tasks: string[] };
      communityResponse: EventResponse;
    },
  ) => void;
  onEventHeaderClick: (event: MyAppointmentsEvent) => void;
  onEventClick: (event: MyAppointmentsEvent) => void;
  search: string;
}

const EventCardComponent: FC<EventCardProps> = ({
  event,
  currentUser,
  community,
  eventResponses,
  tasks,
  eventTypes,
  isLoading,
  zoom = 1,
  onSubmit,
  onEventHeaderClick,
  onEventClick,
  search,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const settings = useSelector(getSettings);
  const [showCommentPopup, setShowCommentPopup] = useState(false);

  const uniqueEventReplies = useMemo((): EventReply[] => {
    const replies = event.subEventReplies || [];
    const userIds = [...new Set(replies.map((r) => r.userId))];

    return userIds
      .map((id) => {
        const userReplies = replies.filter((r) => r.userId === id);

        userReplies.sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );

        if (userReplies.length > 0) {
          return userReplies[0];
        }

        return null;
      })
      .filter((r) => !!r);
  }, [event]);

  const usersEventReply: {
    reply: EventReply & { tasks: string[] };
    communityResponse: EventResponse;
  } = useMemo(() => {
    const reply = uniqueEventReplies.find((r) => r.userId === currentUser?.id);
    const taskReplies = (event.subEventTaskReplies || []).filter(
      (r) => r.userId === currentUser?.id,
    );
    const communityResponse = reply
      ? eventResponses[event.community?.id || event.group?.community?.id]?.find(
          (r) => r.id === reply.eventResponseId,
        )
      : null;

    return {
      reply: {
        ...reply,
        tasks: taskReplies.map((r) => r.eventTaskId),
      },
      communityResponse,
    };
  }, [event, uniqueEventReplies, eventResponses]);

  const [responsesVisible, setResponsesVisible] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    errors,
    formState: { isDirty },
    guestsFieldProps,
    replyFieldProps,
    tasksFieldProps,
    reset,
    getValues,
    values,
  } = useReplyForm(usersEventReply);

  useEffect(() => {
    if (!usersEventReply?.reply?.eventResponseId && !event.isDisabled) {
      setResponsesVisible(true);
    }
  }, []);

  useEffect(() => {
    if (usersEventReply?.reply?.eventResponseId) {
      setResponsesVisible(false);
    }
  }, [uniqueEventReplies]);

  useEffect(() => {
    const {
      reply: {
        eventResponseId = null,
        comment = '',
        tasks = [],
        guests = 0,
        penalty = false,
        attended = false,
      },
    } = usersEventReply;

    reset({
      [FIELDS.EVENT_RESPONSE_ID]: eventResponseId,
      [FIELDS.COMMENT]: comment,
      [FIELDS.TASKS]: tasks,
      [FIELDS.GUESTS]: guests,
      [FIELDS.PENALTY]: penalty,
      [FIELDS.ATTENDED]: attended,
    });
  }, [event]);

  const submitButtonDisabled = useMemo(() => {
    if (isLoading) {
      return true;
    }

    if (!isDirty) {
      //If for was not changed
      return true;
    } else {
      if (replyFieldProps.value) {
        const eventResponse = eventResponses[
          event.community?.id || event.group?.community?.id
        ]?.find((r) => r.id === replyFieldProps.value);

        if (eventResponse.isCommentRequired && !values.comment) {
          //If user selected response, where comment is required, but there's no comment entered
          return true;
        }
      } else if (!usersEventReply?.reply?.eventResponseId) {
        //If user did not select any response and there was no selected response before
        //(if there was a selected response before, button must be shown, because in that case user is trying to delete an existing response)
        return true;
      }
    }

    return false;
  }, [isDirty, isLoading, replyFieldProps]);

  const submitForm = (type: EditEventType) => {
    handleSubmit((data) => {
      onSubmit(type, data, event, usersEventReply);
    })();
  };

  const editSelectionOptions = [
    {
      value: EditEventType.SINGLE_EVENT,
      label: t('event.eventOperations.eventOnly'),
      handleConfirm: () => submitForm(EditEventType.SINGLE_EVENT),
    },
    {
      value: EditEventType.SINGLE_AND_FOLLOW,
      label: t('event.eventOperations.eventAndFollowUp'),
      handleConfirm: () => submitForm(EditEventType.SINGLE_AND_FOLLOW),
    },
    {
      value: EditEventType.FULL_EVENT,
      label: t('event.eventOperations.all'),
      handleConfirm: () => submitForm(EditEventType.FULL_EVENT),
    },
  ];

  const handleExportPdf = () => {
    return generatePdf(
      event,
      community,
      eventTypes,
      (eventResponses && eventResponses[community.id]) || [],
      tasks,
      settings.timeFormat,
      true,
    );
  };

  const communityImage: JSX.Element = useMemo(() => {
    if (event.community?.logo) {
      return <img src={event.community.logo} />;
    } else if (event.group?.community?.logo) {
      return <img src={event.group.community.logo} />;
    }

    return (
      <DefaultCommunityImage
        title={event.community?.name || event.group?.community?.name}
        zoom={zoom}
      />
    );
  }, [event, zoom]);

  return (
    <EventCard zoom={zoom}>
      <form>
        <EventCard.Community
          zoom={zoom}
          onClick={() => onEventHeaderClick(event)}
        >
          <EventCard.Community.ImageBlock zoom={zoom}>
            {communityImage}
          </EventCard.Community.ImageBlock>
          <EventCard.Community.TitleBlock zoom={zoom}>
            <Tooltip
              text={event.community?.name || event.group?.community?.name}
              zoom={zoom}
              isVisible
              tooltipStyle={{
                fontSize: 12 * zoom,
                height: 23 * zoom + 'px',
              }}
              visibleAfterWidth={185 * zoom}
              offset={10 * zoom}
              position="top-center"
            >
              <CommunityTitle zoom={zoom}>
                {event.community?.name || event.group?.community?.name}
              </CommunityTitle>
            </Tooltip>
            {event.group && (
              <GroupTitle zoom={zoom}>{event.group?.name}</GroupTitle>
            )}
          </EventCard.Community.TitleBlock>
        </EventCard.Community>
        <EventCard.Event
          color={event.eventType?.color}
          zoom={zoom}
          onClick={() => onEventClick(event)}
        >
          <EventCard.Event.Header>
            <Tooltip
              text={event.eventType?.title}
              zoom={zoom}
              isVisible
              tooltipStyle={{
                fontSize: 12 * zoom,
                height: 23 * zoom + 'px',
              }}
              visibleAfterWidth={235 * zoom}
              offset={10 * zoom}
              position="top-center"
            >
              <EventCard.Event.EventType zoom={zoom}>
                <span>{event.eventType?.title}</span>
              </EventCard.Event.EventType>
            </Tooltip>
            <EventCard.Event.HeaderIcons zoom={zoom}>
              <EventShareOptionsTooltip
                zoom={zoom}
                onShareClick={(e) => {
                  e.stopPropagation();
                  dispatch(
                    openModal({
                      type: SHARE_POPUP,
                      data: {
                        eventId: event.id,
                        communityId:
                          event.communityId ||
                          event.community?.id ||
                          event.group?.community?.id,
                      },
                    }),
                  );
                }}
                onExportPDFClick={handleExportPdf}
              />
            </EventCard.Event.HeaderIcons>
          </EventCard.Event.Header>
          <Tooltip
            text={event.title}
            zoom={zoom}
            isVisible
            tooltipStyle={{
              fontSize: 12 * zoom,
              display: 'block',
              whiteSpace: 'pre-wrap',
              // overflow: 'hidden',
              lineBreak: 'normal',
              maxWidth: 200 * zoom,
              overflowWrap: 'break-word',
            }}
            visibleAfterHeight={Math.floor(18 * zoom) * 3}
            offset={35 * zoom}
            position="top-center"
          >
            <EventCard.Event.EventTitle zoom={zoom}>
              <HighlightText searchText={search} text={event.title as string} />
            </EventCard.Event.EventTitle>
          </Tooltip>
          <EventCard.Event.Footer>
            <div>
              <EventCard.Event.DateTime zoom={zoom}>
                {`${t(
                  `dayOfWeek.day${moment(event.from).day()}`,
                )}, ${getDateString(new Date(event.from))}`}
              </EventCard.Event.DateTime>
              {!event.allDay && (
                <EventCard.Event.DateTime zoom={zoom}>
                  {getTimeInterval(
                    new Date(event.from),
                    new Date(event.to),
                    settings.timeFormat,
                    t,
                  )}
                </EventCard.Event.DateTime>
              )}
            </div>
            {((event.documents?.length ?? 0) > 0 || event.description) && (
              <EventCard.Event.FooterIcons zoom={zoom}>
                {(event.documents?.length ?? 0) > 0 && (
                  <Tooltip
                    text={t('common.documentation')}
                    zoom={zoom}
                    isVisible
                    tooltipStyle={{
                      fontSize: 12 * zoom,
                      maxWidth: 200 * zoom,
                      whiteSpace: 'pre-wrap',
                      lineBreak: 'normal',
                    }}
                    opacity="0.75"
                    position="left-center"
                  >
                    <EventCard.Event.DocumentIcon zoom={zoom}>
                      <FileIcon />
                    </EventCard.Event.DocumentIcon>
                  </Tooltip>
                )}
                {event.description && (
                  <Tooltip
                    text={event.description}
                    zoom={zoom}
                    isVisible
                    tooltipStyle={{
                      fontSize: 12 * zoom,
                      maxWidth: 200 * zoom,
                      whiteSpace: 'pre-wrap',
                      lineBreak: 'normal',
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word',
                    }}
                    opacity="0.9"
                    position="left-center"
                  >
                    <EventCard.Event.DescriptionIcon zoom={zoom}>
                      <span>i</span>
                    </EventCard.Event.DescriptionIcon>
                  </Tooltip>
                )}
              </EventCard.Event.FooterIcons>
            )}
          </EventCard.Event.Footer>
        </EventCard.Event>
        <EventCard.GreyDivider zoom={zoom} />
        <TasksList
          tasks={tasks}
          zoom={zoom}
          event={event}
          tasksFieldProps={tasksFieldProps}
        />
        <EventCard.GreyDivider zoom={zoom} />
        <ReplyBlock
          eventResponses={eventResponses}
          event={event}
          zoom={zoom}
          replyFieldProps={replyFieldProps}
          usersEventReply={usersEventReply}
          responsesVisible={responsesVisible}
          setResponsesVisible={setResponsesVisible}
          getValues={getValues}
          onCommentRequired={() => {
            setShowCommentPopup(true);
          }}
        />
        <div data-comment-field={event.id}>
          <TextArea
            width="100%"
            label={t('common.addComment')}
            name={FIELDS.COMMENT}
            disabled={
              event.isDisabled && !usersEventReply?.reply?.eventResponseId
            }
            register={register}
            error={errors[FIELDS.COMMENT]}
            zoom={zoom}
            rows={Math.floor(zoom * 5)}
          />
        </div>
        <EventCard.Divider zoom={zoom} />
        <NumberInput
          label={t('event.addGuests')}
          height={Math.floor(39 * zoom) + 'px'}
          width="100%"
          value={guestsFieldProps.value}
          zoom={zoom}
          onAddNumber={() => {
            guestsFieldProps.onChange(guestsFieldProps.value + 1);
          }}
          onSubtractNumber={() => {
            if (guestsFieldProps.value > 0) {
              guestsFieldProps.onChange(guestsFieldProps.value - 1);
            }
          }}
          disabled={event.isDisabled}
        />
        <EventCard.Footer zoom={zoom}>
          {event.eventSeriesId ? (
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            <PopupEventWrapper
              selectionOptions={editSelectionOptions}
              defaultSelectionValue={EditEventType.SINGLE_EVENT}
              popupMessageText={t('event.eventOperations.editReplyText')}
            >
              <Button
                type="button"
                width="100%"
                disabled={submitButtonDisabled}
                zoom={zoom}
              >
                {t('common.answer')}
              </Button>
            </PopupEventWrapper>
          ) : (
            <Button
              type="button"
              width="100%"
              disabled={submitButtonDisabled}
              zoom={zoom}
              onClick={() => submitForm(EditEventType.SINGLE_EVENT)}
            >
              {t('common.answer')}
            </Button>
          )}
        </EventCard.Footer>
      </form>

      <InfoPopupV2
        isOpen={showCommentPopup}
        type="warning"
        headerText={t('bulkUploadEvents.commentRequired.title')}
        messageText={t('bulkUploadEvents.commentRequired.message')}
        defaultButtonText={t('common.ok')}
        withoutCloseButton={true}
        onClose={() => setShowCommentPopup(false)}
        onConfirm={() => {
          setShowCommentPopup(false);
          const commentField = document.querySelector(
            `[data-comment-field="${event.id}"] textarea`,
          ) as HTMLTextAreaElement;
          if (commentField) {
            commentField.focus();
          }
        }}
      />
    </EventCard>
  );
};

export default EventCardComponent;
